import * as yup from 'yup'
import React from 'react'
import { suffixes } from '../choices'
import { Divider } from '../components/divider'

import {
  addressLabel,
  address2Label,
  aliasLabel,
  cellPhoneLabel,
  dobLabel,
  employerLabel,
  employerPhoneLabel,
  firstNameLabel,
  jobTitleLabel,
  homeAddressLabel,
  homePhoneLabel,
  lastNameLabel,
  middleNameLabel,
  optionalLabel,
  personalEmailLabel,
  suffixLabel,
  workEmailLabel,
  workPhoneLabel
} from '../strings/labels'
import {
  addressLengthMessage,
  cityLengthMessage,
  cellPhoneMessage,
  employerPhoneMessage,
  firstNameMessage,
  firstNameLengthMessage,
  homePhoneMessage,
  jobTitleLengthMessage,
  lastNameMessage,
  lastNameLengthMessage,
  maxLength150Message,
  middleNameMessage,
  middleNameLengthMessage,
  personalEmailMessage,
  personalEmailLengthMessage,
  workEmailMessage,
  workEmailLengthMessage,
  workPhoneMessage,
  zipMessage,
  zipLengthMessage
} from '../strings/messages'
import { alphaRegex, phoneRegex, zipRegex } from '../strings/regex'
import { dateYupOptional } from '../validators/dateYup'

const NameHeading = () => <h2 className="ds-u-margin-bottom--0">Name</h2>

const EmployerInformationHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Employment information</h2>
)

const ContactInformationHeading = () => (
  <React.Fragment>
    <h2 className="ds-u-margin-bottom--0">Contact information</h2>
    <p>Please provide as much information as possible</p>
  </React.Fragment>
)

const HomeAddressHeading = () => (
  <h2 className="ds-u-margin-bottom--0">{homeAddressLabel}</h2>
)

export const witnessPage = propOverrides => {
  const props = {
    index: 0,
    maxIndex: 9,
    pageId: 'witness_page',
    title: 'Witness information',
    description:
      'Witness information helps us conduct our investigations. Please provide as much detail as you can. You will be able to enter multiple witnesses.',
    pageAfterWitness: 'narrative_page',
    ...propOverrides
  }

  // Create unique field names based on the field name and the index value.
  const pageId = `${props.pageId}_${props.index}`
  const witness_first_name = `witness_first_name_${props.index}`
  const witness_middle_name = `witness_middle_name_${props.index}`
  const witness_last_name = `witness_last_name_${props.index}`
  const witness_suffix = `witness_suffix_${props.index}`
  const witness_alias_nickname = `witness_alias_nickname_${props.index}`
  const witness_dob = `witness_dob_${props.index}`
  const witness_home_phone = `witness_home_phone_${props.index}`
  const witness_cell_phone = `witness_cell_phone_${props.index}`
  const witness_work_phone = `witness_work_phone_${props.index}`
  const witness_email = `witness_email_${props.index}`
  const witness_work_email = `witness_work_email_${props.index}`
  const witness_address = `witness_address_${props.index}`
  const witness_job_title = `witness_job_title_${props.index}`
  const witness_employer = `witness_employer_${props.index}`
  const witness_employer_address = `witness_employer_address_${props.index}`
  const witness_employer_phone = `witness_employer_phone_${props.index}`

  let page = {
    id: pageId,
    title: props.title,
    description: props.description,
    validation: yup.object().shape({
      [witness_first_name]: yup
        .string()
        .matches(alphaRegex, {
          message: firstNameMessage,
          excludeEmptyString: false
        })
        .max(20, firstNameLengthMessage),
      [witness_middle_name]: yup
        .string()
        .matches(alphaRegex, {
          message: middleNameMessage,
          excludeEmptyString: false
        })
        .max(20, middleNameLengthMessage),
      [witness_last_name]: yup
        .string()
        .matches(alphaRegex, {
          message: lastNameMessage,
          excludeEmptyString: false
        })
        .max(30, lastNameLengthMessage),
      [witness_alias_nickname]: yup.string().max(150, maxLength150Message),
      [witness_dob]: dateYupOptional(),
      [witness_email]: yup
        .string()
        .email(personalEmailMessage)
        .max(250, personalEmailLengthMessage),
      [witness_work_email]: yup
        .string()
        .email(workEmailMessage)
        .max(250, workEmailLengthMessage),
      [witness_cell_phone]: yup.string().matches(phoneRegex, {
        message: cellPhoneMessage,
        excludeEmptyString: false
      }),
      [witness_home_phone]: yup.string().matches(phoneRegex, {
        message: homePhoneMessage,
        excludeEmptyString: false
      }),
      [witness_work_phone]: yup.string().matches(phoneRegex, {
        message: workPhoneMessage,
        excludeEmptyString: false
      }),
      [witness_address]: yup.object().shape({
        line1Value: yup.string().max(50, addressLengthMessage),
        line2Value: yup.string().max(50, addressLengthMessage),
        cityValue: yup.string().max(30, cityLengthMessage),
        zipValue: yup
          .string()
          .max(10, zipLengthMessage)
          .test('zipcode_required', zipMessage, function(value) {
            const { countryValue } = this.parent
            return countryValue === 'US'
              ? value === '' || zipRegex.test(value)
              : true
          })
      }),
      [witness_job_title]: yup.string().max(30, jobTitleLengthMessage),
      [witness_employer_phone]: yup.string().matches(phoneRegex, {
        message: employerPhoneMessage,
        excludeEmptyString: false
      }),
      [witness_employer]: yup.string().max(150, maxLength150Message),
      [witness_employer_address]: yup.object().shape({
        line1Value: yup.string().max(50, addressLengthMessage),
        line2Value: yup.string().max(50, addressLengthMessage),
        cityValue: yup.string().max(30, cityLengthMessage),
        zipValue: yup
          .string()
          .max(10, zipLengthMessage)
          .test('zipcode_required', zipMessage, function(value) {
            const { countryValue } = this.parent
            return countryValue === 'US'
              ? value === '' || zipRegex.test(value)
              : true
          })
      })
    }),
    fields: [
      {
        type: 'content',
        content: NameHeading,
        name: 'name_information'
      },
      {
        type: 'text',
        label: firstNameLabel,
        name: witness_first_name,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: middleNameLabel,
        name: witness_middle_name,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: lastNameLabel,
        name: witness_last_name,
        requirementLabel: optionalLabel
      },
      {
        type: 'select',
        label: suffixLabel,
        name: witness_suffix,
        choices: [
          {
            value: '',
            label: ''
          },
          ...suffixes()
        ],
        size: 'small',
        requirementLabel: optionalLabel
      },
      {
        type: 'content',
        content: Divider,
        name: 'divider_name'
      },
      {
        type: 'text',
        label: aliasLabel,
        name: witness_alias_nickname,
        requirementLabel: optionalLabel
      },
      {
        type: 'date',
        label: dobLabel,
        name: witness_dob,
        requirementLabel: optionalLabel,
        overrideDefaultMinMax: true
      },
      {
        type: 'content',
        content: Divider,
        name: 'divider2'
      },
      {
        type: 'content',
        content: ContactInformationHeading,
        name: 'contact_information'
      },
      {
        type: 'text',
        label: homePhoneLabel,
        name: witness_home_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: cellPhoneLabel,
        name: witness_cell_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: workPhoneLabel,
        name: witness_work_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: personalEmailLabel,
        name: witness_email,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: workEmailLabel,
        name: witness_work_email,
        requirementLabel: optionalLabel
      },
      {
        type: 'content',
        content: Divider,
        name: 'divider3'
      },
      {
        type: 'content',
        content: HomeAddressHeading,
        name: 'mailing_address'
      },
      {
        type: 'address',
        name: witness_address,
        label: '',
        hint: '',
        line1Label: addressLabel,
        line2Label: address2Label,
        line1RequirementLabel: optionalLabel,
        line2RequirementLabel: optionalLabel,
        cityRequirementLabel: optionalLabel,
        stateRequirementLabel: optionalLabel,
        zipRequirementLabel: optionalLabel,
        countryValue: 'US'
      },
      {
        type: 'content',
        content: Divider,
        name: 'divider_employment'
      },
      {
        type: 'content',
        content: EmployerInformationHeading,
        name: 'employer_information'
      },
      {
        type: 'text',
        label: jobTitleLabel,
        name: witness_job_title,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: employerLabel,
        name: witness_employer,
        requirementLabel: optionalLabel
      },
      {
        type: 'address',
        name: witness_employer_address,
        label: '',
        hint: '',
        line1Label: addressLabel,
        line2Label: address2Label,
        line1RequirementLabel: optionalLabel,
        line2RequirementLabel: optionalLabel,
        cityRequirementLabel: optionalLabel,
        stateRequirementLabel: optionalLabel,
        zipRequirementLabel: optionalLabel,
        countryValue: 'US'
      },
      {
        type: 'text',
        label: employerPhoneLabel,
        name: witness_employer_phone,
        size: 'medium',
        requirementLabel: optionalLabel
      }
    ]
  }

  // When not on the last page, give option to add another witness
  if (props.index < props.maxIndex) {
    const add_witness = `add_witness_${props.index}`
    const nextWitnessPage = `${props.pageId}_${props.index + 1}`
    page.fields.push({
      type: 'checkbox',
      label: '',
      name: add_witness,
      choices: [
        {
          label: `Add another witness (up to ${props.maxIndex + 1} witnesses)`,
          value: 'True'
        }
      ],
      requirementLabel: optionalLabel
    })
    page.action = {
      type: 'goto',
      pageId: values => {
        const add = values[add_witness]
        return add.length > 0 ? nextWitnessPage : props.pageAfterWitness
      }
    }
  }

  return page
}
